import {useCallback, useState} from 'react';

type SlideUploaderNameType = 'panel' | 'rail';

type SlideUploadLockType = {
    isPanelUploading: boolean;
    isRailUploading: boolean;
    onPanelUploadingChange: (isUploading: boolean) => void;
    onRailUploadingChange: (isUploading: boolean) => void;
};

// One batch at a time across both slide uploaders: overlapping batches would let the empty-state panel
// unmount mid-flight as soon as the rail adds its first slide, and would make each uploader count the
// remaining slots without knowing about the files of the other one.
//
// A single source of truth rather than a flag per uploader, and the reporting callbacks keep a stable
// identity on purpose: both uploaders report their state from an effect that depends on the callback.
export function useYandexStorySlideUploadLock(): SlideUploadLockType {
    const [uploadingSource, setUploadingSource] = useState<SlideUploaderNameType | null>(null);

    const changeUploadingSource = useCallback((uploader: SlideUploaderNameType, isUploading: boolean): void => {
        setUploadingSource((previous) => {
            if (isUploading) {
                return uploader;
            }

            // Only the uploader that took the lock may release it.
            return previous === uploader ? null : previous;
        });
    }, []);

    const onPanelUploadingChange = useCallback(
        (isUploading: boolean): void => {
            changeUploadingSource('panel', isUploading);
        },
        [changeUploadingSource]
    );

    const onRailUploadingChange = useCallback(
        (isUploading: boolean): void => {
            changeUploadingSource('rail', isUploading);
        },
        [changeUploadingSource]
    );

    return {
        isPanelUploading: uploadingSource === 'panel',
        isRailUploading: uploadingSource === 'rail',
        onPanelUploadingChange,
        onRailUploadingChange,
    };
}
